import * as DialogPrimitive from '@radix-ui/react-dialog'
import { useState, useSyncExternalStore } from 'react'
import { Link } from 'react-router'
import type { ConsentChoice } from '#app/utils/consent/consent'
import {
	chooseConsent,
	closePreferences,
	getConsentRecord,
	hasConsentRecord,
	isPreferencesOpen,
	openPreferences,
	subscribeConsent,
	takeLastFocus,
	wasConsentSaved,
} from '#app/utils/consent/consent.client'
import { siteContent } from '#app/utils/site/content'
import { SITE_LINKS } from '#app/utils/site/site-links'

const copy = siteContent.consent
const neverOnServer = () => false

/**
 * The cookie banner and the Your Privacy Choices dialog, rendered by the root
 * on every page (the reference's `tt-consent-js` UI, styles in
 * app/styles/consent.css). The banner shows until the visitor chooses; the
 * dialog opens from the banner, the footer button or any
 * `[data-tt-consent-open]` element. State lives in consent.client.ts.
 *
 * `hasRecordOnServer` is the root loader's read of the same cookie, so the
 * server renders the banner exactly when the browser will.
 */
export function CookieConsent({
	hasRecordOnServer,
}: {
	hasRecordOnServer: boolean
}) {
	const hasRecord = useSyncExternalStore(
		subscribeConsent,
		hasConsentRecord,
		() => hasRecordOnServer,
	)
	const open = useSyncExternalStore(
		subscribeConsent,
		isPreferencesOpen,
		neverOnServer,
	)
	const saved = useSyncExternalStore(
		subscribeConsent,
		wasConsentSaved,
		neverOnServer,
	)

	return (
		<>
			<output className="sr-only" aria-live="polite">
				{saved ? copy.saved : ''}
			</output>
			{hasRecord ? null : <ConsentBanner />}
			<DialogPrimitive.Root
				open={open}
				onOpenChange={(next) => {
					if (!next) closePreferences()
				}}
			>
				<DialogPrimitive.Portal>
					<DialogPrimitive.Overlay className="ttc ttc-overlay">
						<DialogPrimitive.Content
							className="ttc-dialog"
							onCloseAutoFocus={(event) => {
								event.preventDefault()
								takeLastFocus()?.focus()
							}}
						>
							<PreferencesForm />
						</DialogPrimitive.Content>
					</DialogPrimitive.Overlay>
				</DialogPrimitive.Portal>
			</DialogPrimitive.Root>
		</>
	)
}

function ConsentBanner() {
	return (
		<section className="ttc ttc-banner" aria-label={copy.bannerLabel}>
			<div className="ttc-banner__inner">
				<p className="ttc-banner__text">
					{copy.bannerText}{' '}
					<Link className="ttc-link" to={SITE_LINKS.cookies}>
						{copy.policyLink}
					</Link>
					.
				</p>
				<div className="ttc-banner__actions">
					<button
						type="button"
						className="ttc-btn ttc-btn--primary ttc-eq"
						onClick={() => chooseConsent('accept_all')}
					>
						{copy.acceptAll}
					</button>
					<button
						type="button"
						className="ttc-btn ttc-btn--secondary ttc-eq"
						aria-haspopup="dialog"
						onClick={openPreferences}
					>
						{copy.customize}
					</button>
				</div>
				{/* Closing the bar keeps essential cookies only and remembers it,
				    so it does not come back on the next page (client, 2026-10-05). */}
				<button
					type="button"
					className="ttc-close ttc-banner__close"
					aria-label={copy.bannerClose}
					title={copy.bannerClose}
					onClick={() => chooseConsent('decline')}
				>
					<span className="ttc-close__x" aria-hidden="true" />
				</button>
			</div>
		</section>
	)
}

/** Mounted each time the dialog opens, so the switches start from the saved choice. */
function PreferencesForm() {
	const [draft, setDraft] = useState<ConsentChoice>(() => {
		const saved = getConsentRecord()?.categories
		return {
			preferences: saved?.preferences ?? false,
			analytics: saved?.analytics ?? false,
		}
	})
	const toggle = (key: keyof ConsentChoice) =>
		setDraft((d) => ({ ...d, [key]: !d[key] }))

	return (
		<>
			<div className="ttc-dialog__head">
				<DialogPrimitive.Title className="ttc-dialog__title">
					{copy.dialogTitle}
				</DialogPrimitive.Title>
				<DialogPrimitive.Close className="ttc-close" aria-label={copy.close}>
					<span className="ttc-close__x" aria-hidden="true" />
				</DialogPrimitive.Close>
			</div>
			<div className="ttc-dialog__body">
				<DialogPrimitive.Description className="ttc-dialog__intro">
					{copy.dialogIntro}
				</DialogPrimitive.Description>
				<CategoryCard id="necessary" title={copy.categories.necessary} locked />
				<CategoryCard
					id="preferences"
					title={copy.categories.preferences}
					checked={draft.preferences}
					onToggle={() => toggle('preferences')}
				/>
				<CategoryCard
					id="analytics"
					title={copy.categories.analytics}
					checked={draft.analytics}
					onToggle={() => toggle('analytics')}
				/>
				<p className="ttc-dialog__policy">
					<Link
						className="ttc-link"
						to={SITE_LINKS.cookies}
						onClick={() => {
							// Going to the policy: no focus to hand back, and the banner
							// stays for a visitor who has not chosen yet.
							takeLastFocus()
							closePreferences()
						}}
					>
						{copy.readPolicy}
					</Link>
				</p>
			</div>
			<div className="ttc-dialog__foot">
				<button
					type="button"
					className="ttc-btn ttc-btn--secondary"
					onClick={() => chooseConsent('custom', draft)}
				>
					{copy.save}
				</button>
				<button
					type="button"
					className="ttc-btn ttc-btn--primary ttc-eq"
					onClick={() => chooseConsent('accept_all')}
				>
					{copy.acceptAll}
				</button>
			</div>
		</>
	)
}

function CategoryCard({
	id,
	title,
	locked = false,
	checked = true,
	onToggle,
}: {
	id: string
	title: string
	locked?: boolean
	checked?: boolean
	onToggle?: () => void
}) {
	const titleId = `ttc-cat-${id}`
	return (
		<section className="ttc-cat" data-ttc-category={id}>
			<div className="ttc-cat__head">
				<h3 id={titleId} className="ttc-cat__title">
					{title}
				</h3>
				<button
					type="button"
					role="switch"
					className="ttc-switch"
					aria-checked={checked}
					disabled={locked}
					title={locked ? copy.alwaysOn : undefined}
					aria-label={locked ? `${title}, ${copy.alwaysOn}` : undefined}
					aria-labelledby={locked ? undefined : titleId}
					onClick={onToggle}
				>
					<span className="ttc-switch__thumb" aria-hidden="true" />
				</button>
			</div>
		</section>
	)
}
